import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  PlxsButton,
  PlxsCallout,
  PlxsHeading,
  PlxsText,
  PlxsTextLink,
  PlxsTooltip,
} from "@plexlogic/plexus-ui";
import { apiService, DrawResponse } from "../../services/api";
import { toSentenceCase } from "../../utils/stringUtils";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import { setWizardSession } from "../../services/wizardStorage";
import { formatDateTime } from "../../utils/dateTimeUtils";
import { getTruncatedDisplay } from "../../utils/textUtils";
import {
  getCalloutConfig,
  getTextSnippet,
} from "../../utils/wizardContentRegistry";
import ActivityFeed from "./ActivityFeed";
import DrawStatusInfo from "./DrawStatusInfo";
import DownloadsAndActions from "./DownloadsAndActions";
import WinnersTable from "./WinnersTable";
import WizardSpinner from "../WizardSpinner";
import PaymentStep from "../drawWizardSteps/PaymentStep";
import DrawWizardConfetti from "../DrawWizardConfetti";
import ScheduledLockBanner from "../ScheduledLockBanner";
import { DrawData } from "../../types/DrawData";
import "./DrawDetailComponent.css";

interface DrawDetailParams {
  drawId: string;
}

const DrawDetailComponent: React.FC = () => {
  const { drawId } = useParams<DrawDetailParams>();
  const history = useHistory();
  const { showNotification } = useNotification();
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const [draw, setDraw] = useState<DrawResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Loading states for download actions
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [downloadingResults, setDownloadingResults] = useState(false);
  const [downloadingEntries, setDownloadingEntries] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const [justPaidScheduled, setJustPaidScheduled] = useState(false);
  const [showWinnersReveal, setShowWinnersReveal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Reset mounted ref on mount
    isMountedRef.current = true;

    if (!drawId) {
      if (isMountedRef.current) {
        setError("No draw ID provided");
        setLoading(false);
      }
      return;
    }

    const loadDraw = async () => {
      try {
        if (isMountedRef.current) {
          setLoading(true);
        }
        const drawData = await apiService.getDrawById(drawId);
        if (isMountedRef.current) {
          setDraw(drawData);
        }
      } catch (err) {
        // Interceptor already shows notification
        // Just set error state for UI display
        if (isMountedRef.current) {
          setError("Unable to load draw details");
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };
    loadDraw();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMountedRef.current = false;
    };
  }, [drawId]);

  const handleReturnHome = () => {
    history.push("/");
  };

  const handleDownloadCertificate = async () => {
    if (!draw || downloadingCertificate) return;

    setDownloadingCertificate(true);
    let objectUrl: string | null = null;
    try {
      const blob = await apiService.downloadCertificate(draw.draw_id);
      objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${draw.draw_name}_certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Only show notification if component is still mounted
      if (isMountedRef.current) {
        showNotification(
          "Your file was successfully downloaded",
          "",
          "positive"
        );
      }
    } catch (error: any) {
      // Interceptor already shows notification for API errors
      // No need for duplicate notification here
    } finally {
      // Clean up object URL to prevent memory leaks
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setDownloadingCertificate(false);
      }
    }
  };

  const handleDownloadResultsCsv = async () => {
    if (!draw || downloadingResults) return;

    setDownloadingResults(true);
    let objectUrl: string | null = null;
    try {
      const blob = await apiService.downloadResultsCsv(draw.draw_id);
      objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${draw.draw_name}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Only show notification if component is still mounted
      if (isMountedRef.current) {
        showNotification(
          "Your file was successfully downloaded",
          "",
          "positive"
        );
      }
    } catch (error: any) {
      // Interceptor already shows notification for API errors
      // No need for duplicate notification here
    } finally {
      // Clean up object URL to prevent memory leaks
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setDownloadingResults(false);
      }
    }
  };

  const handleDownloadEntries = async () => {
    if (!draw || !draw.files || draw.files.length === 0 || downloadingEntries)
      return;

    setDownloadingEntries(true);
    let objectUrl: string | null = null;
    try {
      // Download the first entry file (can be modified to allow selection)
      const entryFile = draw.files[0];
      const blob = await apiService.downloadEntryFile(
        draw.draw_id,
        entryFile.file_name
      );
      objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = entryFile.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Only show notification if component is still mounted
      if (isMountedRef.current) {
        showNotification(
          "Your file was successfully downloaded",
          "",
          "positive"
        );
      }
    } catch (error: any) {
      // Interceptor already shows notification for API errors
      // No need for duplicate notification here
    } finally {
      // Clean up object URL to prevent memory leaks
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setDownloadingEntries(false);
      }
    }
  };

  const handleRetryDraw = async () => {
    if (!draw || retrying) return;

    setRetrying(true);
    try {
      // Call API to retry the draw execution
      await apiService.retryDraw(draw.draw_id);

      // Only show notification if component is still mounted
      if (isMountedRef.current) {
        showNotification(
          "Draw retry initiated",
          "Your draw is being processed. You will be notified once completed.",
          "positive"
        );

        // Reload the draw data to get updated status
        const drawData = await apiService.getDrawById(draw.draw_id);
        if (isMountedRef.current) {
          setDraw(drawData);
        }
      }
    } catch (error: any) {
      // Interceptor already shows notification for API errors
      // No need for duplicate notification here
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setRetrying(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    // Use centralized datetime formatting (browser's local timezone)
    const formatted = formatDateTime(dateString);
    return {
      date: formatted.date,
      time: formatted.time,
    };
  };

  if (loading) {
    return (
      <div className="draw-detail-container" data-testid="draw-detail-page">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <WizardSpinner size={48} dataTestId="draw-detail-loading-spinner" />
          <div
            style={{
              marginTop: "var(--plexus-space-md)",
              color: "var(--plexus-color-neutral-300)",
            }}
          >
            <PlxsText>Loading draw details...</PlxsText>
          </div>
        </div>
      </div>
    );
  }

  if (error || !draw) {
    return (
      <div className="draw-detail-container" data-testid="draw-detail-page">
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            marginTop: "var(--plexus-space-2xl)",
          }}
        >
          <PlxsCallout
            dataTestId="error-callout"
            message="Draw not found"
            supportingCopy={
              error ||
              "The draw you are looking for does not exist or has been removed."
            }
            tone="caution"
          />
          <div
            style={{
              marginTop: "var(--plexus-space-lg)",
              display: "flex",
              gap: "var(--plexus-space-md)",
            }}
          >
            {isAuthenticated && (
              <PlxsButton
                dataTestId="draw-detail-error-return-home-button"
                variant="primary"
                label="Return to dashboard"
                onClick={handleReturnHome}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  const formatted = formatDate(draw.created_at);
  const prizeWinners = draw.prizes.reduce(
    (sum, prize) => sum + prize.winners,
    0
  );
  const reserveWinners = draw.prizes.reduce(
    (sum, prize) => sum + prize.reserves,
    0
  );

  // Truncate long draw names with tooltip
  const drawNameDisplay = getTruncatedDisplay(draw.draw_name, 60);

  const paymentRequired = draw.payment_required === true;
  const paywallDrawData: DrawData = {
    drawId: draw.draw_id,
    drawName: draw.draw_name,
    organisation: draw.organisation,
    entryMethod:
      (draw.entry_method as DrawData["entryMethod"]) || "simple",
    entryCount: draw.entry_count,
    prizes: draw.prizes.map((p) => ({
      description: p.description,
      winners: p.winners,
      reserves: p.reserves,
      estimatedValue: p.estimated_value ?? 0,
    })),
    isDemo: false,
    paymentStatus: "unpaid",
  };
  const handlePaywallPaid = async () => {
    try {
      const updated = await apiService.getDrawById(drawId);
      if (!isMountedRef.current) return;
      setDraw(updated);
      if (updated.status === "scheduled") {
        setJustPaidScheduled(true);
        return;
      }
      if (updated.status === "completed") {
        setShowWinnersReveal(true);
        setShowConfetti(true);
        setTimeout(() => {
          if (isMountedRef.current) setShowConfetti(false);
        }, 5000);
      }
    } catch {
    }
  };

  const handleLoginToPay = () => {
    setWizardSession(paywallDrawData, 0);
    sessionStorage.setItem("isRedirectingToLogin", "true");
    loginWithRedirect();
  };

  if (showWinnersReveal) {
    return (
      <div className="draw-detail-container" data-testid="draw-detail-page">
        {showConfetti && <DrawWizardConfetti />}
        <div className="draw-detail-header">
          <PlxsHeading element="h1">
            {toSentenceCase("Your winners")}
          </PlxsHeading>
          {isAuthenticated && (
            <PlxsButton
              dataTestId="winners-reveal-return-home-button"
              functionalIcon="home"
              variant="secondary"
              tone="brand"
              label="Return home"
              onClick={handleReturnHome}
            />
          )}
        </div>
        <div style={{ textAlign: "center", marginBottom: "var(--plexus-space-xl)" }}>
          <div
            style={{
              color: "var(--plexus-color-positive-400)",
              fontWeight: 700,
              fontSize: "2.25rem",
              lineHeight: 1.2,
              marginBottom: "var(--plexus-space-md)",
            }}
          >
            Payment confirmed — congratulations!
          </div>
          <PlxsText size="md">
            Your draw is complete. Here are your winners.
          </PlxsText>
        </div>
        <WinnersTable
          winners={draw.winners || []}
          onDownloadResults={handleDownloadResultsCsv}
          entryMethod={draw.entry_method as DrawData["entryMethod"]}
        />
      </div>
    );
  }

  const isScheduled = draw.status === "scheduled";

  return (
    <div
      className={`draw-detail-container${
        isScheduled ? " has-scheduled-banner" : ""
      }`}
      data-testid="draw-detail-page"
    >
      {isScheduled && (
        <ScheduledLockBanner scheduledDateTime={draw.scheduled_date_time} />
      )}

      {/* Page Header */}
      <div className="draw-detail-header">
        {drawNameDisplay.needsTooltip ? (
          <PlxsTooltip title={drawNameDisplay.fullText}>
            <div style={{ cursor: "help" }}>
              <PlxsHeading element="h1">
                {toSentenceCase("Draw details")}: {drawNameDisplay.displayText}
              </PlxsHeading>
            </div>
          </PlxsTooltip>
        ) : (
          <PlxsHeading element="h1">
            {toSentenceCase("Draw details")}: {drawNameDisplay.displayText}
          </PlxsHeading>
        )}
        {isAuthenticated && (
          <PlxsButton
            dataTestId="draw-detail-return-home-button"
            functionalIcon="home"
            variant="secondary"
            tone="brand"
            label="Return home"
            onClick={handleReturnHome}
          />
        )}
      </div>

      {/* Error Callout for Failed Draws */}
      {draw.status === "failed" &&
        (() => {
          const calloutConfig = getCalloutConfig("drawExecutionFailed");
          const textSnippet = getTextSnippet("drawExecutionFailed");

          return (
            <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
              <PlxsCallout
                dataTestId={calloutConfig.dataTestId || "failed-draw-callout"}
                message={calloutConfig.message}
                supportingCopy={
                  <PlxsText>
                    {textSnippet.prefix}{" "}
                    <PlxsTextLink href={`mailto:${textSnippet.linkText}`}>
                      {textSnippet.linkText}
                    </PlxsTextLink>
                  </PlxsText>
                }
                tone={calloutConfig.tone}
              />
              <div style={{ marginTop: "var(--plexus-space-md)" }}>
                <PlxsButton
                  dataTestId="draw-detail-retry-button"
                  variant="primary"
                  tone="urgent"
                  label="Retry draw"
                  onClick={handleRetryDraw}
                  isLoading={retrying}
                  isDisabled={retrying}
                />
              </div>
            </div>
          );
        })()}

      {/* Main Layout with Activity Feed */}
      <div className="draw-detail-main-layout">
        {/* Main Content */}
        <div className="draw-detail-content">
          {/* Content Grid */}
          <div className="draw-detail-content-grid">
            {/* Left Column - Status & Details */}
            <div>
              <DrawStatusInfo
                status={draw.status}
                date={formatted.date}
                time={formatted.time}
                accountName={draw.organisation}
                drawNo={draw.draw_no}
                drawName={draw.draw_name}
                organisation={draw.organisation}
                entryCount={draw.entry_count || 0}
                prizeTypes={draw.prizes.length}
                prizeWinners={prizeWinners}
                reserveWinners={reserveWinners}
                scheduledDateTime={draw.scheduled_date_time}
                completedDateTime={draw.completed_at}
                executionError={draw.execution_error}
                scrutineerSettings={draw.scrutineer_settings}
                paymentRequired={draw.payment_required}
              />
            </div>

            {/* Right Column - Actions & Features */}
            <div>
              {paymentRequired ? null : (
                <DownloadsAndActions
                  drawStatus={draw.status}
                  files={draw.files || []}
                  onDownloadEntries={handleDownloadEntries}
                  onDownloadResults={handleDownloadResultsCsv}
                  onDownloadCertificate={handleDownloadCertificate}
                  isLoadingEntries={downloadingEntries}
                  isLoadingResults={downloadingResults}
                  isLoadingCertificate={downloadingCertificate}
                />
              )}
            </div>
          </div>

          {paymentRequired ? (
            isAuthenticated ? (
              <div style={{ marginTop: "var(--plexus-space-xl)" }}>
                <PaymentStep
                  drawData={paywallDrawData}
                  onNext={handlePaywallPaid}
                  onBack={handleReturnHome}
                  drawStatus={draw.status}
                />
              </div>
            ) : (
              <div
                data-testid="paywall-login-cta"
                style={{
                  marginTop: "var(--plexus-space-xl)",
                  padding: "var(--plexus-space-xl)",
                  backgroundColor: "var(--plexus-color-surface-50)",
                  borderRadius: "var(--plexus-space-xs)",
                  border:
                    "var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    marginBottom: "var(--plexus-space-md)",
                    color: "var(--plexus-color-neutral-100)",
                  }}
                >
                  Sign in to view your winners
                </div>
                <div
                  style={{
                    marginBottom: "var(--plexus-space-lg)",
                    color: "var(--plexus-color-neutral-200)",
                  }}
                >
                  <PlxsText size="sm">
                    You'll need a free account to pay and reveal results.
                  </PlxsText>
                </div>
                <PlxsButton
                  dataTestId="paywall-login-cta-button"
                  label="Login or create my account"
                  variant="primary"
                  tone="brand"
                  onClick={handleLoginToPay}
                />
              </div>
            )
          ) : justPaidScheduled ? (
            <div
              data-testid="paywall-paid-scheduled-callout"
              style={{ marginTop: "var(--plexus-space-xl)" }}
            >
              <PlxsCallout
                dataTestId="paywall-paid-scheduled-callout-inner"
                tone="positive"
                message="Payment confirmed"
                supportingCopy={
                  <PlxsText size="sm">
                    Your payment has been received. Come back after the
                    draw runs to view and download your winners.
                  </PlxsText>
                }
              />
            </div>
          ) : (
            <WinnersTable
              winners={draw.winners || []}
              onDownloadResults={handleDownloadResultsCsv}
              entryMethod={draw.entry_method as DrawData["entryMethod"]}
            />
          )}
        </div>

        {isAuthenticated && (
          <div className="draw-detail-activity-sidebar">
            <ActivityFeed drawId={drawId} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawDetailComponent;
